import { DetailedDiff, detailedDiff } from 'deep-object-diff';
import { ITVError, ITVErrorCode } from './ITVError';
import { getDiffMessage } from './diffMessage';
import { cloneDeep } from 'lodash-es';

// Matches smart-apply-common `ITV.ts`.
// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
export abstract class ITV<ApplicationData extends Object, Input, Output> {
    protected abstract forwardTransformation(applicationData: ApplicationData): Input;

    protected abstract backwardTransformation(
        output: Output,
        normalizedApplicationData: ApplicationData,
    ): ApplicationData;

    protected normalizeApplicationData(clonedApplicationData: ApplicationData): ApplicationData {
        return clonedApplicationData;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanDiff(diff: DetailedDiff): void {
        return;
    }

    protected compare(
        actualApplication: ApplicationData,
        transformedApplicationData: ApplicationData,
    ): void {
        const diff = detailedDiff(actualApplication, transformedApplicationData);

        this.cleanDiff(diff);

        if (
            Object.keys(diff.added).length ||
            Object.keys(diff.deleted).length ||
            Object.keys(diff.updated).length
        ) {
            throw new ITVError(
                `ITV check failed, ${getDiffMessage(actualApplication, transformedApplicationData, diff, { redactValues: true })}`,
                ITVErrorCode.COMPARISON_FAILED,
            );
        }
    }

    public async execute(
        actualApplicationData: ApplicationData,
        persist: (input: Input) => Promise<Output>,
    ): Promise<void> {
        const clonedApplicationData: ApplicationData = cloneDeep(actualApplicationData);
        const normalizedApplicationData = this.normalizeApplicationData(clonedApplicationData);
        const input = this.forwardTransformation(normalizedApplicationData);

        let output;
        try {
            output = await persist(input);
        } catch (e) {
            if (e instanceof Error) {
                throw new ITVError(e.message, ITVErrorCode.PERSIST_FAILED);
            } else {
                throw new ITVError(
                    'Failed to persist application data',
                    ITVErrorCode.PERSIST_FAILED,
                );
            }
        }

        const transformedApplicationData = this.backwardTransformation(
            output,
            normalizedApplicationData,
        );

        this.compare(normalizedApplicationData, transformedApplicationData);
    }
}
